import React from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Twitter, Mail, Phone, MapPin } from 'lucide-react';

const Footer = () => {
  return (
    <footer data-testid="site-footer" className="bg-gray-900 text-gray-300">
      {/* CTA Section */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-white">
                Transform Your Organization's Performance
              </h3>
              <p className="text-emerald-100 mt-2">
                Let's explore how SAMANVAI can help you achieve industry-leading results.
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/dashboard"
                className="px-6 py-3 bg-white text-emerald-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors shadow-lg"
              >
                Start Assessment
              </Link>
              <Link
                to="/contact"
                className="px-6 py-3 bg-emerald-700 text-white font-semibold rounded-lg hover:bg-emerald-800 transition-colors border border-emerald-500"
              >
                Book a Demo
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="space-y-4">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">S</span>
              </div>
              <span className="text-2xl font-bold text-white">SAMANVAI</span>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed">
              Empowering CXOs with Agentic AI to monitor, control, improve & innovate organizational performance through intelligent benchmarking.
            </p>
            <div className="flex gap-4">
              <a href="#" className="p-2 bg-gray-800 rounded-lg hover:bg-emerald-600 transition-colors">
                <Linkedin className="w-5 h-5" />
              </a>
              <a href="#" className="p-2 bg-gray-800 rounded-lg hover:bg-emerald-600 transition-colors">
                <Twitter className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Solutions */}
          <div>
            <h4 className="text-white font-semibold mb-4">Solutions</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/performance-insights" className="text-gray-400 hover:text-emerald-400 transition-colors text-sm">
                  Performance Insights
                </Link>
              </li>
              <li>
                <Link to="/benchmark-intelligence" className="text-gray-400 hover:text-emerald-400 transition-colors text-sm">
                  Benchmark Intelligence
                </Link>
              </li>
              <li>
                <Link to="/impact-intelligence" className="text-gray-400 hover:text-emerald-400 transition-colors text-sm">
                  Impact Intelligence
                </Link>
              </li>
              <li>
                <Link to="/digital-maturity" className="text-gray-400 hover:text-emerald-400 transition-colors text-sm">
                  Digital Maturity
                </Link>
              </li>
              <li>
                <Link to="/data-maturity" className="flex items-center gap-2 text-gray-400 hover:text-emerald-400 transition-colors text-sm">
                  Data Maturity
                  <span className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">NEW</span>
                </Link>
              </li>
              <li>
                <Link to="/dashboard" className="text-gray-400 hover:text-emerald-400 transition-colors text-sm">
                  Executive Dashboard
                </Link>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-white font-semibold mb-4">Company</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/about" className="text-gray-400 hover:text-emerald-400 transition-colors text-sm">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/resources" className="text-gray-400 hover:text-emerald-400 transition-colors text-sm">
                  Resources
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-400 hover:text-emerald-400 transition-colors text-sm">
                  Contact
                </Link>
              </li>
              <li>
                <Link to="/solution-provider" className="text-gray-400 hover:text-emerald-400 transition-colors text-sm">
                  Become a Provider
                </Link>
              </li>
              <li>
                <a href="#" className="text-gray-400 hover:text-emerald-400 transition-colors text-sm">
                  Careers
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 data-testid="footer-contact-heading" className="text-white font-semibold mb-4">Contact Us</h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-sm">
                <Mail className="w-4 h-4 text-emerald-500" />
                <a data-testid="footer-india-email-link" href="mailto:karna.vijay@gmail.com" className="text-gray-400 hover:text-emerald-400 transition-colors">
                  karna.vijay@gmail.com
                </a>
              </li>
              <li className="flex items-center gap-3 text-sm">
                <Phone className="w-4 h-4 text-emerald-500" />
                <a data-testid="footer-india-phone-link" href="tel:+919963171105" className="text-gray-400 hover:text-emerald-400 transition-colors">
                  +91-9963171105
                </a>
              </li>
              <li className="flex items-start gap-3 text-sm text-gray-400">
                <MapPin className="w-4 h-4 text-emerald-500 mt-0.5" />
                <span data-testid="footer-india-address">Hitech City,<br />Hyderabad, India</span>
              </li>
            </ul>
            <section data-testid="footer-us-office" aria-labelledby="footer-us-office-heading" className="mt-6 pt-6 border-t border-gray-800">
              <h5 id="footer-us-office-heading" data-testid="footer-us-office-heading" className="text-white font-semibold mb-2">US Office</h5>
              <p data-testid="footer-us-office-name" className="text-sm text-gray-300">Amar Reddy</p>
              <p data-testid="footer-us-office-role" className="text-sm text-emerald-400 mt-1 mb-4">Managing Partner</p>
              <address className="not-italic space-y-3 text-sm">
                <div className="flex items-center gap-3">
                  <Phone aria-hidden="true" className="w-4 h-4 flex-shrink-0 text-emerald-500" />
                  <a
                    data-testid="footer-us-office-phone-link"
                    href="tel:+14126525040"
                    className="text-gray-400 hover:text-emerald-400 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-emerald-400"
                  >
                    +1 (412) 652-5040
                  </a>
                </div>
                <div className="flex items-start gap-3 text-gray-400">
                  <MapPin aria-hidden="true" className="w-4 h-4 flex-shrink-0 text-emerald-500 mt-0.5" />
                  <p data-testid="footer-us-office-address" className="min-w-0">
                    21495 Ridgetop Circle<br />
                    Sterling, VA 20166, USA
                  </p>
                </div>
              </address>
            </section>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-gray-500">
            © 2025 SAMANVAI. All rights reserved.
          </p>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-3 text-sm text-gray-500">
            <a href="#" className="hover:text-emerald-400 transition-colors">Privacy Policy</a>
            <a href="#" className="hover:text-emerald-400 transition-colors">Terms of Service</a>
            <a href="#" className="hover:text-emerald-400 transition-colors">Cookie Policy</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
